import React from "react";
import { Link } from "react-router-dom";
import {
  Users, Heart, PhoneCall, ArrowRight, CheckCircle2, Shield,
  MessageCircle, Clock, FileText, Eye, Mail, Camera, Sparkles,
} from "lucide-react";
import { trackPhoneClick } from "../lib/tracking";
import { useSEO, breadcrumbSchema } from "../lib/seo";

const REASSURE = [
  {
    Icon: MessageCircle,
    title: "Plain English, every step",
    text: "No jargon. We explain what's happening, what's next, and what your loved one is choosing.",
  },
  {
    Icon: Clock,
    title: "One business day intake",
    text: "Submit a referral or call us — you'll hear from a real person within one business day.",
  },
  {
    Icon: Camera,
    title: "Real homes, real photos",
    text: "Our SIL homes are photographed honestly. What you see is what your family member moves into.",
  },
  {
    Icon: Shield,
    title: "Worker-screened by design",
    text: "Every team member is NDIS-screened, trauma-informed, and matched on values to your loved one.",
  },
  {
    Icon: Eye,
    title: "Visible at every review",
    text: "Quarterly plan reviews include the people who matter — including you, if your family member chooses.",
  },
  {
    Icon: FileText,
    title: "Clear service agreements",
    text: "Plain-language service agreements. No surprise inclusions, no hidden expectations.",
  },
];

const COMMS = [
  {
    Icon: PhoneCall,
    title: "Call 1300 380 692",
    text: "We answer in business hours. After-hours, our on-call team responds.",
  },
  {
    Icon: Mail,
    title: "Email or written summary",
    text: "Some families prefer written updates. We can send weekly snapshots if that helps.",
  },
  {
    Icon: MessageCircle,
    title: "Coordinator-led liaison",
    text: "If your loved one has a support coordinator, we work seamlessly through them — your choice.",
  },
];

const STEPS = [
  { n: "01", title: "Get in touch", text: "Call us, fill in our form, or chat with our intake assistant." },
  { n: "02", title: "We listen first", text: "What's working today, what's not, what your loved one wants." },
  { n: "03", title: "We propose a fit", text: "A home or a service mix matched to goals — never the other way around." },
  { n: "04", title: "We move at your pace", text: "Visit the home, meet the team, ask anything. No pressure to commit." },
];

const FAQS = [
  {
    q: "Will I be involved in decisions?",
    a: "Always — to the extent your family member chooses. We honour their voice first, then build the family circle they want around it.",
  },
  {
    q: "How quickly can a home be ready?",
    a: "It depends on the home and the participant's needs. Coburg Home is available now; Aintree House is opening soon. We'll be honest about timelines.",
  },
  {
    q: "What happens if something doesn't work?",
    a: "We rebalance — different worker, different shift pattern, different routine. The participant stays in control of who's in their home.",
  },
  {
    q: "Can we visit before deciding?",
    a: "Yes. Most families do. We arrange a private walk-through and meet a member of the support team beforehand.",
  },
];

export default function ForFamiliesPage() {
  useSEO({
    title: "For Families | Alpha Community Care · NDIS support that keeps families informed",
    description:
      "Reassurance for families: real homes, plain-English updates, named coordinators, one-business-day intake. Alpha Community Care — registered NDIS provider across Australia.",
    keywords: [
      "NDIS for families",
      "NDIS family support",
      "SIL home for adult child",
      "family-friendly NDIS provider",
      "registered NDIS provider Melbourne",
      "NDIS plan family communication",
    ],
    canonical: "https://alphacommunitycare.com.au/for-families",
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "For Families", path: "/for-families" },
    ])],
  });

  return (
    <div data-testid="page-for-families">
      {/* HERO */}
      <section className="relative overflow-hidden alpha-gradient-hero alpha-noise border-b border-[color:var(--alpha-line)]">
        <div className="alpha-orb alpha-orb-purple w-[320px] h-[320px] -left-16 top-10 opacity-75 hidden md:block" aria-hidden />
        <div className="alpha-orb alpha-orb-green w-[280px] h-[280px] right-[-40px] top-24 opacity-60 hidden md:block" aria-hidden />
        <div className="alpha-container pt-10 pb-12 md:pt-16 md:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center relative">
          <div className="lg:col-span-7 alpha-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--alpha-line-strong)] bg-white/85 backdrop-blur text-xs font-semibold text-[color:var(--alpha-purple-800)]">
              <Users className="w-3.5 h-3.5" /> For families
            </div>
            <h1 className="mt-5 text-[36px] leading-[1.05] sm:text-5xl lg:text-[60px] font-display font-medium tracking-tight">
              Reassurance, <span className="alpha-serif-italic alpha-gradient-text">not red tape</span>.
            </h1>
            <p className="mt-4 text-[15.5px] md:text-lg text-[color:var(--alpha-mute)] max-w-xl leading-relaxed">
              When the support is right, families relax. We keep you in the loop without taking the participant's voice away — calmly, clearly, in plain English.
            </p>
            <div className="mt-6 md:mt-7 flex flex-wrap gap-2.5 md:gap-3">
              <Link to="/referrals/participant" className="alpha-btn-primary flex-1 sm:flex-none justify-center" data-testid="ff-cta-refer">
                Start a referral <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:1300380692"
                onClick={() => trackPhoneClick("1300 380 692", "for-families-hero")}
                className="alpha-btn-green flex-1 sm:flex-none justify-center"
                data-testid="ff-cta-phone"
              >
                <PhoneCall className="w-4 h-4" /> 1300 380 692
              </a>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-[color:var(--alpha-mute)]">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> 1 business day intake</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Real homes · real photos</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Worker-screened</span>
            </div>
          </div>
          <div className="lg:col-span-5 alpha-fade-up alpha-delay-2">
            <div className="relative rounded-[24px] md:rounded-[28px] overflow-hidden aspect-[4/5] max-h-[500px] shadow-2xl alpha-ring-gradient">
              <img
                src="/homes/coburg/1.jpg"
                alt="Warm sun-lit Alpha SIL home interior with timber floors, exposed brick and a glass-balustrade staircase"
                className="absolute inset-0 w-full h-full object-cover alpha-ken-burns"
                width={960}
                height={1200}
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0625]/45 via-transparent to-transparent" />
              <div className="absolute left-4 right-4 bottom-4 alpha-glass text-[color:var(--alpha-ink)] rounded-2xl p-4 border border-white/40">
                <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[color:var(--alpha-purple-700)]">A real Alpha home</div>
                <div className="mt-1 font-display text-[15px] font-semibold leading-tight">The Coburg Home · VIC</div>
                <div className="mt-2 text-xs text-[color:var(--alpha-mute)]">Accessible SIL · 1:1 & 1:2 support only</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REASSURE GRID */}
      <section className="alpha-section-tight bg-white" data-testid="ff-reassure">
        <div className="alpha-container">
          <div className="max-w-2xl alpha-reveal">
            <div className="alpha-overline">What families tell us matters</div>
            <h2 className="mt-2 font-display text-3xl sm:text-[40px] font-medium tracking-tight leading-[1.05]">
              The hard parts, made <span className="alpha-serif-italic alpha-gradient-text">simpler</span>.
            </h2>
            <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px] leading-relaxed">
              We've sat in too many family meetings to fluff this. Here's what we actually do — and what you can hold us to.
            </p>
          </div>

          <div className="mt-8 md:mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
            {REASSURE.map((r, i) => (
              <div
                key={r.title}
                className="alpha-card p-5 md:p-6 hover:-translate-y-1 transition-transform alpha-reveal-stagger"
                data-testid={`ff-reassure-${i}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-xl alpha-gradient text-white flex items-center justify-center shrink-0 shadow-md">
                  <r.Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-[17px] leading-tight">{r.title}</h3>
                <p className="mt-1.5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMS — how we keep families in the loop */}
      <section className="alpha-section-tight alpha-gradient-soft-band" data-testid="ff-comms">
        <div className="alpha-container">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-7 md:mb-9 alpha-reveal">
            <div className="lg:col-span-7">
              <div className="alpha-overline">How we keep you in the loop</div>
              <h2 className="mt-2 font-display text-3xl sm:text-[40px] font-medium tracking-tight leading-[1.05]">
                Communication, on your terms.
              </h2>
            </div>
            <p className="lg:col-span-5 text-[color:var(--alpha-mute)] text-[15px] leading-relaxed">
              Some families want a weekly call. Others want updates only when something matters. We adapt — guided by what your family member chooses.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 md:gap-4">
            {COMMS.map((c, i) => (
              <div
                key={c.title}
                className="alpha-card p-5 md:p-6 alpha-reveal-stagger hover:-translate-y-1 transition-transform"
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`ff-comms-${i}`}
              >
                <span className="w-9 h-9 rounded-lg bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center">
                  <c.Icon className="w-4.5 h-4.5" />
                </span>
                <h3 className="mt-3.5 font-display font-semibold text-[16px] leading-tight">{c.title}</h3>
                <p className="mt-1.5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="alpha-section-tight bg-white" data-testid="ff-steps">
        <div className="alpha-container">
          <div className="max-w-2xl alpha-reveal">
            <div className="alpha-overline">From hello to settled</div>
            <h2 className="mt-2 font-display text-3xl sm:text-[40px] font-medium tracking-tight leading-[1.05]">
              Four simple steps.
            </h2>
            <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px]">
              No high-pressure sales. We move at the participant's pace, with you alongside.
            </p>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className="alpha-card p-5 md:p-6 alpha-reveal-stagger relative overflow-hidden"
                style={{ transitionDelay: `${i * 70}ms` }}
                data-testid={`ff-step-${i}`}
              >
                <div className="text-[44px] font-display font-medium leading-none text-[color:var(--alpha-purple-200)]" aria-hidden>
                  {s.n}
                </div>
                <h3 className="mt-3 font-display font-semibold text-[16.5px] leading-tight">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="alpha-section-tight alpha-gradient-soft-band" data-testid="ff-faq">
        <div className="alpha-container max-w-3xl">
          <div className="alpha-overline alpha-reveal">Family questions, answered</div>
          <h2 className="mt-2 font-display text-3xl sm:text-[40px] font-medium tracking-tight leading-[1.05] alpha-reveal">
            Honest answers, in plain English.
          </h2>

          <div className="mt-7 md:mt-9 space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={f.q}
                className="alpha-card p-5 md:p-6 group [&_summary::-webkit-details-marker]:hidden"
                data-testid={`ff-faq-${i}`}
              >
                <summary className="cursor-pointer flex items-center justify-between gap-3 font-display font-semibold text-[16px] md:text-[17px] leading-tight">
                  {f.q}
                  <span
                    aria-hidden
                    className="w-7 h-7 rounded-full bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-[14.5px] text-[color:var(--alpha-mute)] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[24px] md:rounded-[36px] alpha-gradient text-white p-8 md:p-12 alpha-noise">
            <div className="alpha-orb alpha-orb-green w-[260px] h-[260px] right-[-40px] top-[-40px] opacity-60 hidden md:block" aria-hidden />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-semibold uppercase tracking-[0.22em]">
                  <Sparkles className="w-3.5 h-3.5" /> Ready when you are
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-[44px] font-medium leading-[1.05] !text-white">
                  Talk to a real person <span className="alpha-serif-italic">today</span>.
                </h2>
                <p className="mt-3 text-white/85 max-w-xl text-[15px]">
                  No call centre. Our intake team picks up — typically within seconds in business hours, and within one business day always.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-2.5">
                <a
                  href="tel:1300380692"
                  onClick={() => trackPhoneClick("1300 380 692", "for-families-final")}
                  className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-purple-50)] transition inline-flex items-center justify-center gap-2"
                  data-testid="ff-final-phone"
                >
                  <PhoneCall className="w-4 h-4" /> 1300 380 692
                </a>
                <Link
                  to="/referrals"
                  className="border-2 border-white/50 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2"
                  data-testid="ff-final-refer"
                >
                  Make a referral <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/easy-read" className="text-white/85 text-center text-sm hover:text-white py-1.5 inline-flex items-center justify-center gap-1.5">
                  View Easy Read <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
